// Phase 2 — Net Payable breakdown modal. Surfaced from BOTH the
// "Total by Partner" table (period=null → all-time) AND the
// "Partner's Payout by Period" table (period=YYYY-MM → that period only).
//
// Rendered as a Modal so it overlays whichever tab the operator is on.
// All numbers come from `breakdownData` which is computed in the parent
// orchestrator (Payouts.jsx) — this file is a pure presentational view.

import { Section, Btn, Modal } from "@/components/Shared";
import { fmtINR, fmtPeriod } from "@/lib/api";

export default function PayoutBreakdownModal({ breakdown, breakdownData, onClose }) {
  return (
    <Modal
      open={!!breakdown}
      onClose={onClose}
      title={breakdown ? `${breakdown.partner_name} · ${breakdown.period ? fmtPeriod(breakdown.period) : "All periods"}` : ""}
      testid="net-explain-modal"
      size="lg"
    >
      {breakdown && breakdownData && (
        <div className="space-y-4">
          <div className="text-[11.5px] text-[var(--muted)]">
            {breakdownData.merchants.length} merchant{breakdownData.merchants.length === 1 ? "" : "s"}{breakdownData.adjs.length ? ` · ${breakdownData.adjs.length} adjustment${breakdownData.adjs.length === 1 ? "" : "s"}` : ""}{breakdown.period ? "" : " across all periods"}
          </div>

          <Section title="Merchants — commission earned" testid="net-explain-merchants">
            <div className="overflow-x-auto max-h-[300px]">
              <table className="table-data w-full">
                <thead><tr>
                  {!breakdown.period && <th>Period</th>}
                  <th>Merchant</th>
                  <th className="text-right">Gross</th>
                  <th className="text-right">Partner Payout</th>
                </tr></thead>
                <tbody>
                  {breakdownData.merchants.map((m) => (
                    <tr key={`${m.period}|${m.merchant_id}|${m.tier || "primary"}`}>
                      {!breakdown.period && <td className="num">{fmtPeriod(m.period)}</td>}
                      <td>
                        <div className="font-medium">{m.merchant_name}</div>
                        <div className="text-[10.5px] text-[var(--muted)] num">{m.merchant_id}{m.gateway ? ` · ${m.gateway}` : ""}{m.lob ? ` · ${m.lob}` : ""}{m.tier === "secondary" ? " · secondary" : ""}</div>
                      </td>
                      <td className="text-right num">{fmtINR(m.gross, 0)}</td>
                      <td className="text-right num text-[var(--accent)] font-semibold">{fmtINR(m.payout, 0)}</td>
                    </tr>
                  ))}
                  {!breakdownData.merchants.length && (
                    <tr><td colSpan={breakdown.period ? 3 : 4} className="text-center text-[var(--muted)] py-4">No merchant rows for this slice.</td></tr>
                  )}
                </tbody>
                <tfoot className="bg-[var(--surface-2)] font-semibold text-sm">
                  <tr>
                    <td colSpan={breakdown.period ? 1 : 2} className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Subtotal</td>
                    <td className="text-right num">{fmtINR(breakdownData.grossTotal, 0)}</td>
                    <td className="text-right num text-[var(--accent)]">{fmtINR(breakdownData.payoutTotal, 0)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Section>

          {breakdownData.adjs.length > 0 && (
            <Section title={`Adjustments — ${breakdownData.adjs.length}`} testid="net-explain-adjustments">
              <div className="overflow-x-auto max-h-[200px]">
                <table className="table-data w-full">
                  <thead><tr>
                    {!breakdown.period && <th>Period</th>}
                    <th>Label</th>
                    <th className="text-right">Amount</th>
                    <th>Notes</th>
                  </tr></thead>
                  <tbody>
                    {breakdownData.adjs.map((a) => (
                      <tr key={a.id}>
                        {!breakdown.period && <td className="num">{fmtPeriod(a.period)}</td>}
                        <td className="font-medium">
                          {a.label}
                          {a.source === "standing" && <span className="ml-1.5 inline-block px-1 py-0 text-[9px] uppercase tracking-wider font-semibold bg-[var(--accent)]/15 text-[var(--accent)] border border-[var(--accent)]/30 rounded-sm">recurring</span>}
                        </td>
                        <td className={`text-right num ${a.amount < 0 ? "text-[var(--negative)]" : "text-[var(--positive)]"}`}>{a.amount < 0 ? "−" : "+"} {fmtINR(Math.abs(a.amount), 0)}</td>
                        <td className="text-[var(--muted)] max-w-xs truncate">{a.notes || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          <div className="bg-[var(--surface-2)] border border-[var(--line-2)] rounded-sm p-3">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--muted)] font-semibold">Commission</div>
                <div className="num text-sm font-semibold mt-0.5">{fmtINR(breakdownData.payoutTotal, 0)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--muted)] font-semibold">+ GST</div>
                <div className="num text-sm font-semibold mt-0.5">{breakdownData.gst > 0 ? fmtINR(breakdownData.gst, 0) : <span className="text-[var(--muted)]">₹ 0</span>}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--muted)] font-semibold">− TDS</div>
                <div className={`num text-sm font-semibold mt-0.5 ${breakdownData.tds > 0 ? "text-[var(--negative)]" : ""}`}>{breakdownData.tds > 0 ? fmtINR(breakdownData.tds, 0) : <span className="text-[var(--muted)]">₹ 0</span>}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--muted)] font-semibold">Adjustments</div>
                <div className={`num text-sm font-semibold mt-0.5 ${breakdownData.adjTotal < 0 ? "text-[var(--negative)]" : breakdownData.adjTotal > 0 ? "text-[var(--positive)]" : ""}`}>{breakdownData.adjTotal ? `${breakdownData.adjTotal < 0 ? "−" : "+"} ${fmtINR(Math.abs(breakdownData.adjTotal), 0)}` : <span className="text-[var(--muted)]">₹ 0</span>}</div>
              </div>
              <div className="bg-[var(--accent)]/10 border border-[var(--accent)]/30 rounded-sm px-2 py-1.5 -m-1">
                <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--accent)] font-semibold">Net Payable</div>
                <div className="num text-base font-bold text-[var(--accent)] mt-0.5" data-testid="net-explain-net-payable">{fmtINR(breakdownData.netPayable, 0)}</div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2 mt-4">
        <Btn variant="ghost" onClick={onClose}>Close</Btn>
      </div>
    </Modal>
  );
}
